import React, { useState } from "react";
import { Dialog } from "@headlessui/react";
import { Button } from "../catalyst/button";
import { useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../config/firebaseConfig";

const updatePasswordFn = httpsCallable(functions, "updatePassword");

type PasswordDialogProps = {
  open: boolean;
  onClose: () => void;
  type: "Update" | "Setup";
  email?: string | undefined | null;
  onConfirm: (password: string) => void;
};

export default function PasswordDialog({
  open,
  onClose,
  type,
  email,
  onConfirm,
}: PasswordDialogProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const isLengthValid = password.length >= 8;
  const hasCapital = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[@$!%*?&]/.test(password);
  const passwordsMatch = password === confirmPassword;
  const [loading, setLoading] = useState(false);

const isValid =
  isLengthValid && hasCapital && hasNumber && hasSpecial && passwordsMatch;

  const regex =
    /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const validate = () => {

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return false;
    }

    setError("");
    return true;
  };

  const handleConfirm = async () => {
    if (!validate()) return;
  
    try {
      setLoading(true);
      setError("");
  
      await updatePasswordFn({ password });
  
      onClose(); // ✅ close ONLY on success
  
    } catch (err: any) {
      console.error("Password update failed:", err);
  
      setError(
        err?.message || "Something went wrong. Please try again."
      );
  
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!open) {
      setPassword("");
      setConfirmPassword("");
      setError("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50" static={false}>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Dialog */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-sm rounded-lg bg-[#111827] text-white p-6 shadow-lg border border-[#1f2937]">
          
          <Dialog.Title className="text-lg font-semibold mb-2">
            {type === "Update"
              ? "Update password"
              : "Set up email & password login"}
          </Dialog.Title>

          {type === "Setup" && (
            <Dialog.Description className="text-sm text-gray-400 mb-4">
              Setting up a password lets you sign in with your email
              {email && ` (${email})`} and password.
            </Dialog.Description>
          )}

          {/* Password */}
          <input
            type="password"
            placeholder={type === "Update" ? "New password" : "Password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`w-full bg-[#0f172a] border ${
              error ? "border-red-500" : "border-slate-600"
            } rounded-lg px-3 py-2 text-white mb-2`}
          />

          {/* Confirm Password */}
          <input
            type="password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={`w-full bg-[#0f172a] border ${
              error ? "border-red-500" : "border-slate-600"
            } rounded-lg px-3 py-2 text-white mb-2`}
          />

          {/* Password rules */}
          <ul className="text-xs mb-2 space-y-1">
            <li className={password.length >= 8 ? "text-green-400" : "text-gray-400"}>
              • At least 8 characters
            </li>
            <li className={/[A-Z]/.test(password) ? "text-green-400" : "text-gray-400"}>
              • One capital letter
            </li>
            <li className={/\d/.test(password) ? "text-green-400" : "text-gray-400"}>
              • One number
            </li>
            <li className={/[@$!%*?&]/.test(password) ? "text-green-400" : "text-gray-400"}>
              • One special character
            </li>
          </ul>

          {/* Error */}
          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

          {/* Buttons */}
          <div className="flex justify-end gap-2 mt-4">
            <Button color="darktheme" onClick={onClose} type="button">
              Cancel
            </Button>

            <button
  type="button"
  onClick={handleConfirm}
  disabled={!isValid || loading}
  className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
    isValid
      ? "bg-brand-600"
      : "bg-brand-600/50 cursor-not-allowed"
  }`}
>
  {loading ? "Saving..." : "Confirm"}
</button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}